import Role from '../../types/role';
import { Assigned, OperationFilter, QueryGroup } from '../../services/types/Operation';
import FilterConfig from './FilterConfig';

export const getDefaultQueryGroup = (role: Role, assigned: string) => {
  if (assigned !== Assigned.AllOp) {
    return QueryGroup.All;
  }
  return role === Role.QrGroup ? QueryGroup.Prep : QueryGroup.All;
};

// "Default 3" filter rules
export const getDefaultSubAssigned = (role: Role, assigned: string, queryGroup: QueryGroup) => {
  if (assigned !== Assigned.AllOp) {
    return null;
  }
  switch (queryGroup) {
    case QueryGroup.All:
      return null;
    case QueryGroup.Prep: {
      if (role === Role.QrGroup) {
        return 'notAssigned';
      }
      if ([Role.FrontOffice, Role.GroupHead].includes(role)) {
        return 'assigned';
      }
      return null;
    }
    case QueryGroup.Exec:
      return role === Role.QrGroup ? 'notAssigned' : null;
    case QueryGroup.Cc:
      return [Role.GroupHead, Role.Specialist].includes(role) ? 'assigned' : null;
    default:
      return null;
  }
};

export const getInitState = (
  roleCode: Role,
  isOperRole: Assigned,
  getHideColumns: (filter: OperationFilter) => string[],
  getRemoveColumns: (filter: OperationFilter) => string[]
) => {
  const queryGroup = getDefaultQueryGroup(roleCode, isOperRole);
  const subqueryAssigned = getDefaultSubAssigned(roleCode, isOperRole, queryGroup);
  const filter: OperationFilter = {
    assigned: isOperRole,
    queryGroup,
    subqueryType: subqueryAssigned,
    subqueryId: null,
    pageNumber: 1,
    pageSize: 10,
    sortField: 'Number',
    sortDescending: false,
    hideColumns: [],
    removeColumns: [],
  };
  filter.hideColumns = getHideColumns(filter);
  filter.removeColumns = getRemoveColumns(filter);
  return filter;
};

export const getColumnConfigs = () => {
  const commonList = [
    'operationYear',
    'teamLeader',
    'esgDocumentStatus',
    'assessmentsDisclosure',
    'analysisMissionDate',
    'approvalDate',
    'idbAmount',
    'totalProjectCost',
    'subSectorName',
    'executingAgency',
    'borrower',
    'previousEsrr',
    'previousEsrrDate',
    'previousEsrrRationale',
    'sprRationale',
    'prevSpr',
    'sprRationale',
    'prevSprDate',
    'prevSprRationale',
    'ghgEmissionsOperation',
    'ghgEmissionsConstruction',
    'primaryMember',
  ];

  const hideForAllFilter: string[] = [
    ...commonList,
    'isReformulated',
    'espsPolicies',
    'ermDate',
    'qrrDate',
    'kickOffDate',
    'm95DisbursedDate',
    'miciCases',
    'esgIndicators',
  ];
  const hideForCustomFilter: string[] = [
    ...commonList,
    'esgStatus',
    'spr',
    'sprDate',
    'nextCommittee',
    'nextMission',
    'lastMission',
    'percDisburstment',
    'pmr',
    'closingDate',
  ];

  const commonAssigned = [
    'esgStatus',
    'operationYear',
    'spr',
    'sprDate',
    'sprRationale',
    'nextCommittee',
    'nextMission',
    'teamLeader',
    'esgDocumentStatus',
    'assessmentsDisclosure',
    'analysisMissionDate',
    'nextMission',
    'lastMission',
    'approvalDate',
    'idbAmount',
    'totalProjectCost',
    'percDisburstment',
    'pmr',
    'subSectorName',
    'executingAgency',
    'borrower',
    'closingDate',
    'phase',
    'isReformulated',
    'espsPolicies',
    'previousEsrr',
    'prevSpr',
    'ermDate',
    'qrrDate',
    'kickOffDate',
    'm95DisbursedDate',
    'miciCases',
    'ghgEmissionsOperation',
    'ghgEmissionsConstruction',
  ];

  const commonExec = [
    'esgStatus',
    'operationYear',
    'nextCommittee',
    'nextMission',
    'teamLeader',
    'esgDocumentStatus',
    'assessmentsDisclosure',
    'analysisMissionDate',
    'nextMission',
    'approvalDate',
    'idbAmount',
    'totalProjectCost',
    'subSectorName',
    'executingAgency',
    'borrower',
    'closingDate',
    'phase',
    'isReformulated',
    'espsPolicies',
    'previousEsrr',
    'prevSpr',
    'ermDate',
    'qrrDate',
    'kickOffDate',
    'm95DisbursedDate',
    'miciCases',
    'ghgEmissionsOperation',
    'ghgEmissionsConstruction',
    'sprRationale',
  ];

  const notAssignedConfigs = [
    new FilterConfig(QueryGroup.All, Assigned.AllOp, null, hideForAllFilter, []),
    new FilterConfig(QueryGroup.All, Assigned.MyOp, null, hideForAllFilter, []),
    new FilterConfig(QueryGroup.All, Assigned.GroupOp, null, hideForAllFilter, []),
    new FilterConfig(QueryGroup.All, Assigned.AllOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.All, Assigned.MyOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.All, Assigned.GroupOp, 'customFilters', hideForCustomFilter, []),

    new FilterConfig(
      QueryGroup.Prep,
      Assigned.AllOp,
      null,
      [
        ...hideForCustomFilter,
        'isReformulated',
        'espsPolicies',
        'ermDate',
        'qrrDate',
        'kickOffDate',
        'm95DisbursedDate',
        'miciCases',
      ],
      [
        'esgStatus',
        'phase',
        'percDisburstment',
        'spr',
        'sprDate',
        'sprRationale',
        'prevSpr',
        'kickOffDate',
        'pmr',
        'm95DisbursedDate',
        'closingDate',
        'miciCases',
        '_all',
      ]
    ),
    new FilterConfig(QueryGroup.Prep, Assigned.AllOp, 'notAssigned', commonAssigned, [
      'esgStatus',
      'phase',
      'percDisburstment',
      'spr',
      'sprDate',
      'sprRationale',
      'prevSpr',
      'groupName',
      'esgMembers',
      'kickOffDate',
      'pmr',
      'm95DisbursedDate',
      'closingDate',
      'miciCases',
      '_notAssigned',
    ]),
    new FilterConfig(QueryGroup.Prep, Assigned.AllOp, 'assigned', commonAssigned, [
      'esgStatus',
      'phase',
      'percDisburstment',
      'spr',
      'sprDate',
      'sprRationale',
      'prevSpr',
      'kickOffDate',
      'pmr',
      'm95DisbursedDate',
      'closingDate',
      'miciCases',
      '_assigned',
    ]),
    new FilterConfig(QueryGroup.Prep, Assigned.AllOp, 'byGroup', commonAssigned, [
      'esgStatus',
      'phase',
      'percDisburstment',
      'spr',
      'sprDate',
      'sprRationale',
      'prevSpr',
      'kickOffDate',
      'pmr',
      'm95DisbursedDate',
      'closingDate',
      'miciCases',
      '_byGroup',
    ]),
    new FilterConfig(QueryGroup.Prep, Assigned.AllOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Prep, Assigned.MyOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Prep, Assigned.GroupOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Prep, Assigned.GroupOp, null, hideForAllFilter, []),

    new FilterConfig(
      QueryGroup.Exec,
      Assigned.AllOp,
      null,
      [...commonExec, 'sprRationale'],
      ['esgStatus', 'phase', '_all']
    ),
    new FilterConfig(
      QueryGroup.Exec,
      Assigned.AllOp,
      'notAssigned',
      [...commonExec, 'esgMembers', 'sprRationale'],
      ['esgStatus', 'phase', 'groupName', 'esgMembers', '_notAssigned']
    ),
    new FilterConfig(
      QueryGroup.Exec,
      Assigned.AllOp,
      'assigned',
      [...commonExec, 'sprRationale'],
      ['esgStatus', 'phase', '_assigned']
    ),
    new FilterConfig(QueryGroup.Exec, Assigned.AllOp, 'byGroup', commonExec, [
      'esgStatus',
      'phase',
      '_byGroup',
    ]),
    new FilterConfig(QueryGroup.Exec, Assigned.AllOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Exec, Assigned.MyOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Exec, Assigned.GroupOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Exec, Assigned.GroupOp, null, hideForAllFilter, []),

    new FilterConfig(
      QueryGroup.Cc,
      Assigned.AllOp,
      null,
      [
        'operationYear',
        'nextCommittee',
        'nextMission',
        'teamLeader',
        'esgDocumentStatus',
        'assessmentsDisclosure',
        'analysisMissionDate',
        'nextMission',
        'approvalDate',
        'idbAmount',
        'totalProjectCost',
        'subSectorName',
        'executingAgency',
        'borrower',
        'phase',
        'isReformulated',
        'espsPolicies',
        'previousEsrr',
        'prevSpr',
        'ermDate',
        'qrrDate',
        'kickOffDate',
        'm95DisbursedDate',
        'miciCases',
        'ghgEmissionsOperation',
        'ghgEmissionsConstruction',
        'sprRationale',
      ],
      ['_all']
    ),
    new FilterConfig(
      QueryGroup.Cc,
      Assigned.AllOp,
      'notAssigned',
      [
        'operationYear',
        'nextCommittee',
        'nextMission',
        'teamLeader',
        'esgDocumentStatus',
        'assessmentsDisclosure',
        'analysisMissionDate',
        'nextMission',
        'approvalDate',
        'idbAmount',
        'totalProjectCost',
        'subSectorName',
        'executingAgency',
        'borrower',
        'phase',
        'isReformulated',
        'espsPolicies',
        'previousEsrr',
        'prevSpr',
        'ermDate',
        'qrrDate',
        'kickOffDate',
        'm95DisbursedDate',
        'miciCases',
        'ghgEmissionsOperation',
        'ghgEmissionsConstruction',
        'sprRationale',
      ],
      ['groupName', 'esgMembers', '_notAssigned']
    ),
    new FilterConfig(
      QueryGroup.Cc,
      Assigned.AllOp,
      'assigned',
      [
        'operationYear',
        'nextCommittee',
        'nextMission',
        'teamLeader',
        'esgDocumentStatus',
        'assessmentsDisclosure',
        'analysisMissionDate',
        'nextMission',
        'approvalDate',
        'idbAmount',
        'totalProjectCost',
        'subSectorName',
        'executingAgency',
        'borrower',
        'phase',
        'isReformulated',
        'espsPolicies',
        'previousEsrr',
        'prevSpr',
        'ermDate',
        'qrrDate',
        'kickOffDate',
        'm95DisbursedDate',
        'miciCases',
        'ghgEmissionsOperation',
        'ghgEmissionsConstruction',
        'sprRationale',
      ],
      ['_assigned']
    ),
    new FilterConfig(
      QueryGroup.Cc,
      Assigned.AllOp,
      'byGroup',
      [
        'operationYear',
        'nextCommittee',
        'nextMission',
        'teamLeader',
        'esgDocumentStatus',
        'assessmentsDisclosure',
        'analysisMissionDate',
        'nextMission',
        'approvalDate',
        'idbAmount',
        'totalProjectCost',
        'subSectorName',
        'executingAgency',
        'borrower',
        'phase',
        'isReformulated',
        'espsPolicies',
        'previousEsrr',
        'prevSpr',
        'ermDate',
        'qrrDate',
        'kickOffDate',
        'm95DisbursedDate',
        'miciCases',
        'ghgEmissionsOperation',
        'ghgEmissionsConstruction',
        'sprRationale',
      ],
      ['_byGroup']
    ),
    new FilterConfig(QueryGroup.Cc, Assigned.AllOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Cc, Assigned.MyOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Cc, Assigned.GroupOp, 'customFilters', hideForCustomFilter, []),
    new FilterConfig(QueryGroup.Cc, Assigned.GroupOp, null, hideForAllFilter, []),
  ];

  const assignedConfigs = notAssignedConfigs
    .filter((x) => x.subqueryType === 'assigned')
    .map(
      (x) => new FilterConfig(x.queryGroup, Assigned.MyOp, null, x.hideColumns, x.removeColumns)
    );

  return [...notAssignedConfigs, ...assignedConfigs];
};
